import { useState } from 'react';

type SetStateAction<S> = S | ((prev: S) => S);
type MaybePromise<T> = T | Promise<T>;

export function useOptimisticState<S>(initialState: S) {
  const [state, setState] = useState<S>(initialState);

  const optimisticSetState = (
    newStateOrPromise: MaybePromise<SetStateAction<S>>,
    optimisticState?: S
  ): void | Promise<void> => {
    if (newStateOrPromise instanceof Promise) {
      const prevState = state;

      if (optimisticState !== undefined) {
        setState(optimisticState);
      }

      return newStateOrPromise
        .then(newState => {
          if (newState !== undefined) {
            setState(typeof newState === 'function' ? (newState as (prev: S) => S)(prevState) : newState);
          }
        })
        .catch(error => {
          setState(prevState);
          throw error;
        });
    } else {
      return setState(newStateOrPromise);
    }
  };

  return [state, optimisticSetState] as const;
}
